import { Clock3, PhoneCall, PhoneForwarded, PhoneOff } from 'lucide-react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import { ShimmerSkeleton } from '../../../components/animations/Skeleton';
import { Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CallStats } from '../types';

function formatDuration(seconds: number) {
  const roundedSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(roundedSeconds / 60);
  return `${minutes}:${String(roundedSeconds % 60).padStart(2, '0')}`;
}

export function CallMetrics({ stats, loading }: Readonly<{ stats?: CallStats; loading: boolean }>) {
  const { t } = useTranslation();
  const metrics = [
    {
      key: 'total',
      label: t('voiceAgents.callsList.metricsTotal'),
      value: stats?.total ?? 0,
      icon: PhoneCall,
      color: colors.primary.neon,
      background: colors.primary.muted,
    },
    {
      key: 'active',
      label: t('voiceAgents.callsList.metricsActive'),
      value: stats?.inProgress ?? 0,
      icon: PhoneForwarded,
      color: colors.status.info.main,
      background: colors.status.info.bg,
    },
    {
      key: 'completed',
      label: t('voiceAgents.callsList.metricsCompleted'),
      value: stats?.completed ?? 0,
      icon: PhoneCall,
      color: colors.status.success.main,
      background: colors.status.success.bg,
    },
    {
      key: 'no-answer',
      label: t('voiceAgents.callsList.metricsNoAnswer'),
      value: stats?.noAnswer ?? 0,
      icon: PhoneOff,
      color: colors.text.tertiary,
      background: colors.surface.row,
    },
    {
      key: 'duration',
      label: t('voiceAgents.callsList.metricsAvgDuration'),
      value: formatDuration(stats?.avgDurationSeconds ?? 0),
      icon: Clock3,
      color: colors.accent.purple[400],
      background: colors.metrics.conversionSoft,
    },
  ];

  return (
    <View testID="calls-metrics" style={styles.wrapper} accessibilityRole="summary">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <View
              key={metric.key}
              style={styles.card}
              accessible
              accessibilityLabel={`${metric.label}: ${String(metric.value)}`}
            >
              <View style={[styles.iconWell, { backgroundColor: metric.background }]}>
                <Icon size={16} color={metric.color} strokeWidth={1.8} />
              </View>
              <VStack spacing="xxs" style={styles.copy}>
                {loading ? (
                  <ShimmerSkeleton width={46} height={20} borderRadius={4} />
                ) : (
                  <Text selectable style={styles.value} color={colors.text.primary}>
                    {metric.value}
                  </Text>
                )}
                <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
                  {metric.label}
                </Text>
              </VStack>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexShrink: 0,
  },
  row: {
    gap: tokens.spacing.sm,
  },
  card: {
    minWidth: 148,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.smPlus,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.smPlus,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
  },
  iconWell: {
    width: 34,
    height: 34,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    minWidth: 0,
  },
  value: {
    fontSize: tokens.fontSize.lg,
    lineHeight: 20,
    fontWeight: tokens.fontWeight.bold,
    fontVariant: ['tabular-nums'],
  },
});
